import { z } from 'zod';

import { idSchema, inlineImageSchema, isoDateTimeSchema } from './common';
import { dateBucketSchema } from './rest/stats';

/**
 * Background job contracts: the BullMQ queue names, job names and payload shapes
 * shared by the producer (`apps/api`) and the consumer (`apps/worker`).
 *
 * This belongs in `contracts` rather than inside the worker because a job payload
 * is a wire format like any other. The two processes deploy independently, and a
 * payload that is already sitting in Redis outlives the deploy that enqueued it —
 * so the worker parses every payload against these schemas instead of trusting
 * the shape. A drifted payload then fails loudly at the queue boundary rather
 * than scoring the wrong post from an `undefined` id.
 *
 * Pure TypeScript + Zod, like the rest of this package: the dashboard imports it
 * straight from source into a browser bundle.
 */

// ---------------------------------------------------------------------------
// Queues
// ---------------------------------------------------------------------------

/**
 * Every queue in the system.
 *
 * Deduplication is deliberately absent: the API deduplicates and upserts a batch
 * synchronously before acknowledging it, and only then enqueues scoring. Moving
 * dedup behind a queue would reintroduce exactly the race that
 * `UNIQUE(agent_id, batch_id)` exists to prevent.
 *
 * These strings are Redis key material. Renaming one orphans every job already
 * queued under the old name, so they are frozen once shipped.
 */
export const QUEUE_NAMES = {
  /** Tier-1 rule scoring. Runs for every ingested or rediscovered post. */
  SCORING: 'scoring',
  /** Tier-2 AI scoring. Only posts above the project's rule threshold. */
  AI_SCORING: 'ai-scoring',
  /** AI comment drafting. */
  AI_COMMENT: 'ai-comment',
  /**
   * Reading a post's publication time off a screenshot, when the parser could not.
   *
   * Its own queue rather than a job name on `AI_SCORING`, because the two must
   * not compete for the same slots. Time extraction is a repair job triggered by
   * a parser miss: a Facebook layout change makes it spike to *every* post in a
   * batch at once, and sharing a queue would let that spike push the scoring
   * that the product actually runs on to the back of the line.
   */
  AI_POST_TIME: 'ai-post-time',
  /** Per-project daily aggregates for the dashboard charts. */
  STATS: 'stats',
  /** Expiry of diagnostic screenshots and other time-bounded objects. */
  RETENTION: 'retention',
} as const;

export const QUEUE_NAME_VALUES = [
  QUEUE_NAMES.SCORING,
  QUEUE_NAMES.AI_SCORING,
  QUEUE_NAMES.AI_COMMENT,
  QUEUE_NAMES.AI_POST_TIME,
  QUEUE_NAMES.STATS,
  QUEUE_NAMES.RETENTION,
] as const;

export const queueNameSchema = z.enum(QUEUE_NAME_VALUES);
export type QueueName = (typeof QUEUE_NAME_VALUES)[number];

// ---------------------------------------------------------------------------
// Job names
// ---------------------------------------------------------------------------

/**
 * The job name inside a queue. BullMQ uses it to route to a named processor and
 * to group metrics; one queue may carry several. Like the queue names, these are
 * persisted in Redis with each job and must stay stable.
 */
export const JOB_NAMES = {
  SCORE_POST_MATCH: 'score-post-match',
  AI_SCORE_POST_MATCH: 'ai-score-post-match',
  GENERATE_COMMENT: 'generate-comment',
  RESOLVE_POST_TIME: 'resolve-post-time',
  RECOMPUTE_PROJECT_STATS: 'recompute-project-stats',
  PURGE_EXPIRED_OBJECTS: 'purge-expired-objects',
} as const;

export const JOB_NAME_VALUES = [
  JOB_NAMES.SCORE_POST_MATCH,
  JOB_NAMES.AI_SCORE_POST_MATCH,
  JOB_NAMES.GENERATE_COMMENT,
  JOB_NAMES.RESOLVE_POST_TIME,
  JOB_NAMES.RECOMPUTE_PROJECT_STATS,
  JOB_NAMES.PURGE_EXPIRED_OBJECTS,
] as const;

export const jobNameSchema = z.enum(JOB_NAME_VALUES);
export type JobName = (typeof JOB_NAME_VALUES)[number];

// ---------------------------------------------------------------------------
// scoring · score-post-match
// ---------------------------------------------------------------------------

/**
 * Why a post match is being (re)scored.
 *
 * Scores are append-only revisions, so every revision can name the event that
 * produced it. Without that, a user looking at three revisions of the same post
 * has no way to tell a routine rediscovery from an operator override.
 */
export const SCORE_TRIGGER_REASONS = [
  /** The post is new to this project — first revision. */
  'INGESTED',
  /** Seen again in a later cycle; counts or content were refreshed. */
  'REDISCOVERED',
  /** A human asked for a re-score from the dashboard. */
  'MANUAL_RESCORE',
  /** The project's keywords or negative keywords changed under existing matches. */
  'KEYWORD_CHANGED',
] as const;

export const scoreTriggerReasonSchema = z.enum(SCORE_TRIGGER_REASONS);
export type ScoreTriggerReason = (typeof SCORE_TRIGGER_REASONS)[number];

/**
 * Payload of a tier-1 scoring job.
 *
 * It carries ids only, never a copy of the post. The row is authoritative and may
 * have changed between enqueue and execution — a job that embedded the content
 * would happily score a stale copy.
 *
 * `projectId` is redundant with `postMatchId` on purpose: the worker asserts the
 * two agree, which catches a stale or mis-addressed payload before it writes a
 * score row against the wrong project's keywords.
 */
export const scorePostMatchJobSchema = z.object({
  postMatchId: idSchema,
  projectId: idSchema,
  reason: scoreTriggerReasonSchema,
});
export type ScorePostMatchJob = z.infer<typeof scorePostMatchJobSchema>;

// ---------------------------------------------------------------------------
// Custom job ids
// ---------------------------------------------------------------------------

/**
 * Separator for every composite job id in the system.
 *
 * It is NOT a colon. BullMQ rejects a custom id containing `:` unless the id
 * happens to split into exactly three segments — a backwards-compatibility path
 * for repeatable jobs, not a contract to build on. `__` never occurs in a cuid,
 * so a composite id can still be split back apart unambiguously.
 */
const JOB_ID_SEPARATOR = '__';

/**
 * Would BullMQ accept this string as a custom job id?
 *
 * Both rules are lifted from bullmq 5.x `Job.validateOptions`
 * (`dist/cjs/classes/job.js`): an id that round-trips through `parseInt` is
 * rejected as "Custom Ids cannot be integers", and an id containing `:` is
 * rejected as "Custom Id cannot contain :" unless `split(':').length === 3`.
 * We are stricter than BullMQ on the second rule — no colon at all — because the
 * three-segment exemption is an accident of history, not a promise.
 *
 * The rule is duplicated here rather than left to BullMQ on purpose. BullMQ only
 * applies it while the job is being added, where the producer's error handler
 * cannot tell a permanent programming error apart from a transient queue outage
 * and will treat both as "Redis is having a bad day". Validating where the id is
 * *built* keeps that distinction available to the caller.
 */
export function isValidJobId(jobId: string): boolean {
  if (jobId.length === 0) return false;
  if (`${Number.parseInt(jobId, 10)}` === jobId) return false;
  return !jobId.includes(':');
}

/** Build a job id, refusing to hand back one the queue would reject. */
function buildJobId(...segments: string[]): string {
  const jobId = segments.join(JOB_ID_SEPARATOR);
  if (!isValidJobId(jobId)) {
    throw new Error(`invalid BullMQ job id: ${JSON.stringify(jobId)}`);
  }
  return jobId;
}

/**
 * Job id for a tier-1 rule scoring job.
 *
 * The reason is part of the id so that a manual re-score is not silently
 * deduplicated against the ingestion score it is meant to supersede, while a
 * resent batch still collapses onto one job.
 *
 * `run` exists because that is only half the story. BullMQ keeps completed jobs
 * under their id, and adding an id that already exists is a silent no-op — so a
 * reason alone made a re-score a ONE-SHOT: the second invocation enqueued 21
 * jobs, BullMQ dropped all 21, and `post_scores` sat at revision 1 while the
 * script reported success. Any caller that must actually run again passes a
 * token that changes between runs and stays fixed within one, so a retry inside
 * the same run still collapses.
 *
 * Ingestion passes nothing: two deliveries of one batch SHOULD collapse.
 */
export function buildScoringJobId(
  postMatchId: string,
  reason: ScoreTriggerReason,
  run?: string,
): string {
  return run === undefined
    ? buildJobId(postMatchId, reason)
    : buildJobId(postMatchId, reason, run);
}

/**
 * Job id for the tier-2 AI hand-off.
 *
 * Deliberately reason-free: a paid model call must not be repeated just because
 * the cheap tier ran again for a different reason.
 */
export function buildAiScoringJobId(postMatchId: string): string {
  return buildJobId('ai', postMatchId);
}

// ---------------------------------------------------------------------------
// stats · recompute-project-stats
// ---------------------------------------------------------------------------

/**
 * Payload of a daily statistics recompute.
 *
 * `day` is a calendar day (`YYYY-MM-DD`) in the workspace timezone, not a
 * timestamp: the dashboard buckets by day, and a job payload is JSON in Redis
 * where a `Date` would arrive as a string anyway. Reusing `dateBucketSchema`
 * keeps the format identical to the one `/stats/*` returns, so a chart bucket and
 * a job payload can never disagree about what a day is.
 */
export const recomputeProjectStatsJobSchema = z.object({
  projectId: idSchema,
  day: dateBucketSchema,
});
export type RecomputeProjectStatsJob = z.infer<typeof recomputeProjectStatsJobSchema>;

/**
 * The payloads for `ai-scoring` and `retention` are defined alongside the phases
 * that introduce them. Their queue names live here so that the API, the worker
 * and any future scheduler agree on one spelling.
 */

// ---------------------------------------------------------------------------
// ai-comment · generate-comment
// ---------------------------------------------------------------------------

/**
 * Who asked for this draft — and therefore who writes the candidate row.
 *
 * This is the one field the `ai-comment` worker branches on, because the two
 * callers want genuinely different things from the same model call:
 *
 *  - `API` — a person clicked "sinh comment" and an HTTP request is blocked on
 *    `waitUntilFinished`. The worker returns {@link AiCommentDraftResult} and
 *    writes NO `comment_candidates` row: the API already owns that write, and
 *    it is the only side that knows the request-scoped overrides the operator
 *    typed. Two writers would race for one row.
 *  - `AUTO` — the post crossed the project's score threshold on its own and
 *    nobody is waiting. There is no HTTP response to return anything to, so the
 *    worker validates the draft and writes the candidate itself.
 *
 * Defaulted to `API` so a payload enqueued by an older API build — one that
 * predates this field and is already sitting in Redis — still decodes to the
 * behaviour it was written for, rather than silently switching to the branch
 * that writes rows.
 */
export const AI_COMMENT_JOB_ORIGINS = ['API', 'AUTO'] as const;
export const aiCommentJobOriginSchema = z.enum(AI_COMMENT_JOB_ORIGINS);
export type AiCommentJobOrigin = (typeof AI_COMMENT_JOB_ORIGINS)[number];

/**
 * Payload of a comment-drafting job.
 *
 * Ids only, like every other payload here: the project's comment settings are
 * read by the worker at execution time, never carried in the job. A payload that
 * embedded `maxCommentLength` would draft against a limit the operator changed
 * ten minutes ago and no screen would explain the mismatch.
 *
 * The optional overrides (`style`, `maxLength`, `includeLink`,
 * `extraInstructions`, `templateId`) come from `GenerateCommentRequest` and are
 * meaningful only for `origin: 'API'`. `null` means "no override — use the
 * project's setting", which is why they are nullable rather than absent: an
 * `AUTO` job has no operator to have expressed a preference.
 */
export const generateCommentJobSchema = z.object({
  origin: aiCommentJobOriginSchema.default('API'),
  /**
   * The workspace the producer BELIEVES this post belongs to.
   *
   * Nullable because the automatic producer sits in the worker, downstream of a
   * scoring summary that carries ids rather than a workspace. The consumer never
   * trusts it either way: it reads the workspace off the project row and, when
   * this field is present, refuses the job if the two disagree. A payload that
   * chose the workspace would be choosing which customer's API key to spend.
   */
  workspaceId: idSchema.nullable().default(null),
  projectId: idSchema,
  postMatchId: idSchema,
  /** Null for `AUTO`: no human asked. */
  requestedByUserId: idSchema.nullable().default(null),
  /**
   * Currently RECORDED BUT UNUSED. `buildCommentPrompt` shapes tone from
   * `comment_settings.brand_voice` and has no style slot, so honouring this
   * would mean inventing prompt text next to a model whose output gets
   * published. Kept in the payload so the value is not lost when the prompt
   * grows a slot for it.
   */
  style: z.enum(['FRIENDLY', 'PROFESSIONAL', 'CONCISE', 'ENTHUSIASTIC']).nullable().default(null),
  /** Narrows the project's `maxCommentLength`; never widens it. */
  maxLength: z.number().int().positive().nullable().default(null),
  /** Narrows the project's `minCommentLength`; never widens it. */
  minLength: z.number().int().positive().nullable().default(null),
  /** Null = follow `comment_settings.allow_links`. False can only forbid. */
  includeLink: z.boolean().nullable().default(null),
  /**
   * Currently RECORDED BUT UNUSED, and deliberately so: free text from an
   * operator would be spliced into a prompt whose answer is posted under their
   * own name, and the prompt has no fenced slot that keeps it distinguishable
   * from the hard rules. See the note in the worker.
   */
  extraInstructions: z.string().max(1_000).nullable().default(null),
  templateId: idSchema.nullable().default(null),
});
export type GenerateCommentJob = z.infer<typeof generateCommentJobSchema>;

/**
 * What the worker hands back for an `API`-origin job.
 *
 * `declined` is a first-class answer, not a failure: the model is allowed to say
 * "this post should not be commented on", the request still cost money and
 * `ai_requests` still records SUCCESS. `comment` is empty exactly then — the
 * parser in `@midiautolistening/ai` erases any text attached to a refusal — so
 * the two fields are cross-checked rather than trusted independently.
 */
export const aiCommentDraftResultSchema = z
  .object({
    comment: z.string(),
    declined: z.boolean().default(false),
    confidence: z.number().min(0).max(1),
    requiresManualReview: z.boolean(),
    /** Vietnamese, for the operator's review queue. Never posted to Facebook. */
    reason: z.string(),
    selectedImageId: z.string().nullable().default(null),
    aiRequestId: z.string().nullable().default(null),
  })
  .superRefine((value, context) => {
    if (!value.declined && value.comment.trim().length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['comment'],
        message: 'comment must not be empty unless the draft was declined',
      });
    }
  });
export type AiCommentDraftResult = z.infer<typeof aiCommentDraftResultSchema>;

/**
 * Job id for an automatic comment draft.
 *
 * Reason-free and post-scoped, for the same reason as the tier-2 hand-off: every
 * re-score of the same post would otherwise buy another draft. An `API`-origin
 * job passes NO custom id — an operator who presses the button twice is asking
 * for two drafts, and collapsing them would leave the second request hanging on
 * a job that already completed.
 */
export function buildAiCommentJobId(postMatchId: string): string {
  return buildJobId('aicomment', postMatchId);
}

// ---------------------------------------------------------------------------
// ai-post-time · resolve-post-time
// ---------------------------------------------------------------------------

/**
 * Payload for reading a post's publication time off a screenshot.
 *
 * THE ONE PLACE IN THIS FILE THAT CARRIES BYTES, and it needs a justification
 * because every other payload here is ids only.
 *
 * The reason ids do not work: the picture is evidence that existed for one
 * instant on someone else's machine. There is no row it could be read back from,
 * because the whole point is that the parser produced nothing to store. The
 * alternative — upload the crop to object storage from the ingestion path, then
 * pass a key — buys durability nobody wants (the image is worthless the moment
 * the question is answered) at the cost of a write, a signed read, and a sweeper
 * for the ones whose job never ran. A few kilobytes bounded by
 * `INLINE_IMAGE_MAX_BYTES` is the cheaper honest answer.
 *
 * `capturedAt` is NOT decoration and NOT interchangeable with the job's own
 * clock. Facebook writes relative labels — "16 giờ", "2 ngày trước" — and a
 * relative label only means something against the instant it was read. A job
 * that sat in the queue for twenty minutes behind a backlog would resolve "16
 * giờ" to twenty minutes too late if the worker used `now`, and nothing
 * downstream could ever detect the drift.
 */
export const resolvePostTimeJobSchema = z.object({
  /**
   * Read off the project row by the producer and re-checked by the consumer
   * against the post's own project — the same rule as the comment job, for the
   * same reason: this field decides whose API key pays.
   */
  workspaceId: idSchema,
  projectId: idSchema,
  /** The global post row whose `posted_at` is missing. */
  postId: idSchema,
  /**
   * The match that carried the image, for the audit trail and for `ai_requests`.
   * Nullable because a post can be rediscovered by a second project while this
   * job waits, and the row that produced the picture is the one that should be
   * credited with it.
   */
  postMatchId: idSchema.nullable().default(null),
  /** The cropped timestamp region, as the agent saw it. */
  image: inlineImageSchema,
  /**
   * What the parser managed to read but could not convert, when it read
   * anything. Given to the model as a hint and used afterwards to sanity-check
   * the answer: a model that returns a date while the label plainly said "3
   * ngày trước" has read something other than what was on screen.
   */
  rawLabel: z.string().trim().max(120).nullable().default(null),
  /** When the agent took the screenshot. The anchor for every relative label. */
  capturedAt: isoDateTimeSchema,
});
export type ResolvePostTimeJob = z.infer<typeof resolvePostTimeJobSchema>;

/**
 * Job id for a time-resolution job — scoped to the post, with no reason segment.
 *
 * Post-scoped rather than match-scoped on purpose. `posts.posted_at` is a
 * property of the post itself, shared by every project that found it, so three
 * projects discovering the same undated post in one cycle must buy **one**
 * answer, not three. The match id still rides in the payload for the audit
 * trail; it just does not get a vote on identity.
 */
export function buildResolvePostTimeJobId(postId: string): string {
  return buildJobId('posttime', postId);
}
